import { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { MetricCard } from '../components/common/atoms'
import { RoundedSelect } from '../components/tailwind/atoms'
import { handleAppError } from '../components/handlers'
import { AppDispatchContext } from '../utils/contexts'
import { ContractsManager } from '../managers'
import { utilizationApi } from '../services'
import { CardSlider } from '../components/tailwind/molecules'
import { OverviewApi } from '../services'
import { commaingNumber, computeRating } from '../utils'
import { HealthChallengeCard, OverviewServiceCard, StepsCard, WellnessCalendarCard } from '../components/tailwind/organisms'
import { BENEFITS, NOT_ENABLED_BENEFITS } from '../constants/overview'
import { DownloadPngButton, SectionCard } from '../components/opdClaims/primitives'
import useReloadOnRelease from '../utils/useReloadOnRelease'
import {
  OverviewChevronIcon,
  OverviewLockIcon,
  OverviewRatingStar,
  OverviewSectionIcon,
} from '../components/overview/OverviewSectionIcon'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'

const SPONSORED = 'sponsored'
const NON_SPONSORED = 'non-sponsored'

const BENEFIT_DISPLAY_NAMES = {
  ahc: 'Health checks',
  consultations: 'Doctor consultation',
  dental: 'Dental',
  vision: 'Vision',
  hra: 'HRA',
  pre_employment: 'Pre employment',
}

const LOW_ADOPTION_THRESHOLD = 25

/** Uppercase section kicker used between page chapters. */
const Kicker = ({ children, className = '' }) => (
  <div className={`text-[11px] font-fig-semibold uppercase tracking-[0.1em] text-gray-600 ${className}`}>
    {children}
  </div>
)

const Overview = () => {
  const [state, setState] = useState({ notSponsoredConsultation: false, contractDetails: {}, enabledBenefits: {}, notEnabledServices: [], benefitsUtilizationPercentage: { hra: 1000, pre_employment: 999 }, openModel: false })
  const dispatch = useContext(AppDispatchContext)
  const history = useHistory()
  const attentionRef = useRef(null)

  const hrFirstName = useMemo(() => {
    const hr_name = sessionStorage.getItem('hr_name')
    if(!hr_name) return ''

    let hrName = hr_name.split(' ')
    return hrName.length > 0 ? hrName[0] : ''
  }, [])

  const setSubState = (newState) => {
    setState(prevState => { return {...prevState, ...newState} })
  }

  useEffect(() => {
    fetchContractDetails()
  }, [])

  useReloadOnRelease()

  const setUilizationToBenefit = (type, benefitType, percentage) => {
    if(!BENEFITS.includes(benefitType)) return

    let { benefitsUtilizationPercentage = {} } = state

    if(benefitsUtilizationPercentage[benefitType] === percentage) return

    benefitsUtilizationPercentage[benefitType] = percentage
    setSubState({ benefitsUtilizationPercentage })

    sortBenefitsByUtilization(type, benefitsUtilizationPercentage)
  }

  const sortBenefitsByUtilization = (type, benefitsUtilizationPercentage) => {
    benefitsUtilizationPercentage = benefitsUtilizationPercentage ?? state.benefitsUtilizationPercentage

    if(type === SPONSORED) {
      let { sponsoredBenefits = [] } = state.enabledBenefits
      sponsoredBenefits = sponsoredBenefits.sort((a, b) => (benefitsUtilizationPercentage[b] ?? 0) - (benefitsUtilizationPercentage[a] ?? 0))

      setSubState({ enabledBenefits: {...state.enabledBenefits, sponsoredBenefits} })
    } else if(type === NON_SPONSORED) {
      let { nonSponsoredBenefits = [] } = state.enabledBenefits
      nonSponsoredBenefits = nonSponsoredBenefits.sort((a, b) => (benefitsUtilizationPercentage[b] ?? 0) - (benefitsUtilizationPercentage[a] ?? 0))

      setSubState({ enabledBenefits: {...state.enabledBenefits, nonSponsoredBenefits} })
    }
  }

  const fetchContractDetails = async () => {
    await utilizationApi.fetchContracts({},
      (resp) => {
        var contractObj = new ContractsManager(resp.data)
        const contract_id = Number(sessionStorage.getItem('contract_id'))
        const selectedContract = contractObj.formattedData.find(item => item.contract_id === contract_id) || contractObj.formattedData[0]
        const showSpendingAccount = selectedContract.showSpendingOrRemoteWallet
        const contractDetails = {
          contracts: contractObj.formattedData,
          selectedContract
        }
        setSubState({ contractDetails })
        dispatch({ type: '', showSpendingAccount })
        if(showSpendingAccount) {
          sessionStorage.setItem('isOPDAccount', showSpendingAccount);
          history.replace('/opd-overview')
          return
        }
        fetchOverviewInitData(selectedContract.contract_id, selectedContract.start_date, selectedContract.end_date, contractDetails)
      },
      (err) => handleAppError(err)
    )
  }

  const fetchOverviewInitData = async (contractId, startDate, endDate, contractDetails) => {
    contractDetails = contractDetails ?? state.contractDetails
    const contract_id = contractId ?? contractDetails.selectedContract.contract_id
    const start_date = startDate ?? contractDetails.selectedContract.start_date
    const end_date = endDate ?? contractDetails.selectedContract.end_date

    await OverviewApi.fetchOverviewInitData({ contract_id, start_date, end_date },
      (res) => {
        const enabledBenefits = res.data.enabled_benefits ?? []

        let sponsoredBenefits = enabledBenefits.filter(item => item.primary === 'primary_sponsored')
        let nonSponsoredBenefits = enabledBenefits.filter(item => item.primary !== 'primary_sponsored')

        let notSponsoredConsultation = nonSponsoredBenefits.map(service => service.benefit_type).includes('consultation24x7')
        sponsoredBenefits = sponsoredBenefits.map(service => service.benefit_type === 'consultation24x7' ? 'consultations' : service.benefit_type).filter(e => BENEFITS.includes(e)) // filtering benefits that are to display
        sponsoredBenefits = ['hra', ...new Set(sponsoredBenefits.includes('pre_employment') ? ['pre_employment', ...sponsoredBenefits] : sponsoredBenefits)]

        nonSponsoredBenefits = nonSponsoredBenefits.map(service => service.benefit_type).filter(e => BENEFITS.includes(e)) // filtering benefits that are to display
        nonSponsoredBenefits = [...new Set(nonSponsoredBenefits.includes('pre_employment') ? ['pre_employment', ...nonSponsoredBenefits] : nonSponsoredBenefits)]

        if(state?.contractDetails?.selectedContract?.contract_id !== contract_id) {
          contractDetails = {
            ...contractDetails,
            selectedContract: contractDetails?.contracts?.find(item => item.contract_id === contract_id)
          }
          setSubState({ enabledBenefits: { sponsoredBenefits, nonSponsoredBenefits }, notSponsoredConsultation: notSponsoredConsultation, contractDetails })
        } else setSubState({ enabledBenefits: { sponsoredBenefits, nonSponsoredBenefits }, notSponsoredConsultation: notSponsoredConsultation })

        callApis(contract_id, start_date, end_date)
      },
      (err) => handleAppError(err)
    )
  }

  const callApis = (contractId, startDate, endDate) => {
    fetchOverviewMetric(contractId, startDate, endDate)
    fetchOverviewCostAndRatings(contractId, startDate, endDate)
    fetchNotEnabledServices(contractId, startDate, endDate)
  }

  const fetchOverviewMetric = async (contractId, startDate, endDate) => {
    const contract_id = contractId || state.contractDetails.selectedContract.contract_id
    const start_date = startDate || state.contractDetails.selectedContract.start_date
    const end_date = endDate || state.contractDetails.selectedContract.end_date

    await OverviewApi.fetchOverviewMetric({ contract_id, start_date, end_date },
      (res) => {
        setSubState({...res.data})
      },
      (err) => handleAppError(err)
    )
  }

  const fetchOverviewCostAndRatings = async (contractId, startDate, endDate) => {
    const contract_id = contractId || state.contractDetails.selectedContract.contract_id
    const start_date = startDate || state.contractDetails.selectedContract.start_date
    const end_date = endDate || state.contractDetails.selectedContract.end_date

    await OverviewApi.fetchOverviewCostAndRatings({ contract_id, start_date, end_date },
      (res) => {
        setSubState({...res.data})
      },
      (err) => handleAppError(err)
    )
  }

  const fetchNotEnabledServices = async (contract_id, start_date, end_date) => {
    contract_id = contract_id || state.contractDetails.selectedContract.contract_id
    start_date = start_date || state.contractDetails.selectedContract.start_date
    end_date = end_date || state.contractDetails.selectedContract.end_date

    await OverviewApi.fetchNotEnabledServices({ contract_id, start_date, end_date },
      (resp) => {
        setSubState({
          notEnabledServices: resp.data || []
        })
      },
      (err) => {
        handleAppError(err)
      }
    )
  }

  const changeContractTo = (contractId) => {
    const contract = state.contractDetails.contracts.find((item) => item.contract_id == contractId)
    sessionStorage.setItem('contract_id', contract.contract_id);
    const showSpendingAccount = contract.showSpendingOrRemoteWallet
    if(showSpendingAccount) {
      sessionStorage.setItem('isOPDAccount', showSpendingAccount);
      history.replace('/opd-overview')
      return
    }
    fetchOverviewInitData(contract.contract_id, contract.start_date, contract.end_date)
  }

  const toggleModal = () => setSubState({ openModel: !state.openModel })

  const { contractDetails: { contracts = [], selectedContract = {} }, notEnabledServices = [], enabledBenefits: { sponsoredBenefits = [], nonSponsoredBenefits = []},
          active_customers_count = 0, onboarded_customers_count = 0, total_spendings = 0, total_ratings = 0, notSponsoredConsultation = false } = state
  const { contract_id: selectedContractId, start_date: selectedContractStartDate, end_date: selectedContractEndDate, is_running_contract: isRunningContract, uuid: selectedContractUuid } = selectedContract
  const totalSpendings = commaingNumber(total_spendings)
  const totalVerifiedRating = Object.values(total_ratings).reduce((a,b) => a + b, 0)
  const [totalRating, verifiedRatings] = computeRating(total_ratings)
  const utilizationPercentage = active_customers_count > 0 ? (onboarded_customers_count / active_customers_count) * 100 : 0
  const notEnabledServicesKeys = Object.keys(NOT_ENABLED_BENEFITS)
  const filteredNotEnabledServices = notEnabledServices.filter(e => notEnabledServicesKeys.includes(e.service))
  const noOfNotEnabledServices = filteredNotEnabledServices.length

  // Utilization percentages arrive per benefit via setUilizationToBenefit; the
  // initial hra/pre_employment values are sort sentinels (>100), so only
  // 0-100 values are real measurements.
  const realBenefitUtilizations = Object.entries(state.benefitsUtilizationPercentage ?? {})
    .filter(([, pct]) => typeof pct === 'number' && pct >= 0 && pct <= 100)

  // ---- "Needs your attention" items, derived from data already on the page ----
  const lowAdoptionItems = realBenefitUtilizations
    .filter(([, pct]) => pct < LOW_ADOPTION_THRESHOLD)
    .sort((a, b) => a[1] - b[1])
    .map(([benefit, pct]) => ({
      key: `low-${benefit}`,
      accent: 'var(--ds-amber)',
      title: `${BENEFIT_DISPLAY_NAMES[benefit] ?? benefit} adoption is low`,
      body: <>Only <span className='font-jetbrains-mono font-semibold tabular-nums'>{pct}%</span> of employees have used it so far.</>,
      ctaText: 'Send a reminder →',
      onCta: () => history.push('/benefit-adoption-mailers'),
    }))
  const notEnabledItem = noOfNotEnabledServices > 0
    ? [{
      key: 'not-enabled',
      accent: 'var(--ds-blue)',
      title: `${noOfNotEnabledServices} ${noOfNotEnabledServices > 1 ? 'services' : 'service'} not enabled`,
      body: <>{filteredNotEnabledServices.map(item => NOT_ENABLED_BENEFITS[item.service]['service_type']).join(', ')} — popular add-ons your employees don't have yet.</>,
      ctaText: 'View services →',
      onCta: () => setSubState({ openModel: true }),
    }]
    : []
  const attentionItems = [...lowAdoptionItems, ...notEnabledItem].slice(0, 3)

  const benefitsEnabledCount = sponsoredBenefits.length + nonSponsoredBenefits.length + (notSponsoredConsultation ? 1 : 0)

  const notEnabledServicesData = filteredNotEnabledServices.map((item) =>
    <MetricCard
      loading={false}
      loaderHeight={120}
      icon={<OverviewSectionIcon name={NOT_ENABLED_BENEFITS[item.service].iconKey} />}
      title={ <span className='font-semibold text-lg text-gray-700'>{NOT_ENABLED_BENEFITS[item.service]['service_type']}</span> }
      subTitle = {<div className='flex flex-row items-center mb-2'> <OverviewRatingStar className='mr-1 h-3.5 w-3.5 shrink-0 text-amber-400' /> <span className='font-semibold text-base text-gray-600'>{item.rating}</span>/5 {`(${item.verified_ratings} verified ratings`})</div>}
      subTitleClasses = 'text-gray-600 font-normal text-sm ml-1'
      metricValue={<p className='text-sm font-normal text-gray-600'><span className='font-semibold text-base text-gray-600'>{commaingNumber(item.total_utilization)}</span> {`total ${NOT_ENABLED_BENEFITS[item.service]['tag']} availed in last one year`}</p>}
      classes={`w-90 inline-block m-3 flex-1 h-36`} metricClasses = 'flex' headerClasses='px-6 py-4' svgClasses='h-8 w-8 mb-4'
    />
  )

  return (
    <div className={`pt-4 text-gray-700 ${noOfNotEnabledServices > 0 ? 'mb-20' : ''}`}>
      {/* Header */}
      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div className='min-w-0'>
          <div className='text-[11px] font-fig-semibold uppercase tracking-[0.12em] text-blue-700'>
            Datavue · Benefits utilisation
          </div>
          <h1 className='mb-0 mt-2 font-fig-extrabold text-4xl leading-tight tracking-tight text-gray-900'>Overview</h1>
          <p className='mb-0 mt-2 max-w-2xl text-sm font-fig-normal leading-relaxed text-gray-600'>
            {`Hi ${hrFirstName}, track utilisation & analytics of all your enabled benefits.`}
          </p>
        </div>
        <RoundedSelect
          listClasses='w-56'
          selectOptions={contracts.map(i => i.text)}
          selectValues={contracts.map(i => i.contract_id)}
          curvalue={contracts.find(i => i.contract_id === selectedContractId)?.text}
          selectHandler={changeContractTo}
        />
      </div>

      {/* Needs your attention */}
      {attentionItems.length > 0 && (
        <SectionCard ref={attentionRef} className='mt-6'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-2'>
              <div className='text-sm font-fig-semibold text-gray-900'>Needs your attention</div>
              <DownloadPngButton nodeRef={attentionRef} fileName='needs-your-attention' />
            </div>
            <div className='font-jetbrains-mono text-xs tabular-nums text-gray-500'>{attentionItems.length} {attentionItems.length > 1 ? 'items' : 'item'}</div>
          </div>
          <div className='mt-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3'>
            {attentionItems.map((item) => (
              <div
                key={item.key}
                className='rounded-[10px] border border-gray-300 p-4'
                style={{ borderLeft: `3px solid ${item.accent}` }}
              >
                <div className='text-[13px] font-fig-semibold text-gray-900'>{item.title}</div>
                <p className='mb-0 mt-1 text-xs font-fig-normal leading-relaxed text-gray-700'>{item.body}</p>
                <button
                  type='button'
                  className='mt-2 border-0 bg-transparent p-0 text-xs font-fig-semibold text-blue-700 hover:text-blue-800 focus:outline-none'
                  onClick={item.onCta}
                >
                  {item.ctaText}
                </button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* KPI tiles */}
      <div className='mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4'>
        <MetricCard
          variant='ds-default'
          hideMetricIcon
          title='Activation percentage'
          subTitle='Percentage of employees who logged in at least once on the ekincare app'
          metricValue={`${Math.round(utilizationPercentage)}%`}
          metricValueSuffix={
            <>
              <span className='font-jetbrains-mono tabular-nums text-gray-900'>{commaingNumber(onboarded_customers_count)}</span>
              <span className='text-gray-500'> / </span>
              <span className='font-jetbrains-mono tabular-nums text-gray-900'>{commaingNumber(active_customers_count)}</span>
              <span className='text-gray-600'> employees</span>
            </>
          }
          loading={false}
        />
        {!!totalSpendings && (
          <MetricCard
            variant='ds-default'
            hideMetricIcon
            title='Total amount spent'
            subTitle='The amount spent on the sponsored services on your current plan'
            metricValue={`₹${totalSpendings}`}
            loading={false}
          />
        )}
        {!!verifiedRatings && (
          <MetricCard
            variant='ds-default'
            hideMetricIcon
            title='Overall rating'
            subTitle='Average of the ratings across the service lines'
            metricValue={`${totalRating}/5`}
            metricValueSuffix={`${commaingNumber(totalVerifiedRating)} verified ratings`}
            loading={false}
          />
        )}
        {benefitsEnabledCount > 0 && (
          <MetricCard
            variant='ds-default'
            hideMetricIcon
            title='Benefits enabled'
            subTitle='Sponsored & non-sponsored benefits active on this contract'
            metricValue={`${benefitsEnabledCount}`}
            loading={false}
          />
        )}
      </div>

      {/* Services — full detail */}
      <Kicker className='mt-8'>All services — full detail</Kicker>
      {sponsoredBenefits.map((item, index) => {
        return <OverviewServiceCard  key={item + index} classes='mt-4' benefitType={item} contractId={selectedContractId} startDate={selectedContractStartDate} endDate={selectedContractEndDate} contractUuid={selectedContractUuid} totalCustomerCount={active_customers_count} isRunningContract={isRunningContract}  setUilizationToBenefit={setUilizationToBenefit.bind('', SPONSORED, item)} />
      })}
      {notSponsoredConsultation ? <OverviewServiceCard  key={'consultations999'} classes='mt-4' benefitType='consultations' contractId={selectedContractId} startDate={selectedContractStartDate} endDate={selectedContractEndDate} contractUuid={selectedContractUuid} totalCustomerCount={active_customers_count} isRunningContract={isRunningContract} setUilizationToBenefit={setUilizationToBenefit.bind('', SPONSORED, 'consultations')} /> : null }

      <Kicker className='mt-8'>Wellness calendar</Kicker>
      <WellnessCalendarCard classes='mt-4' contractId={selectedContractId} startDate={selectedContractStartDate} endDate={selectedContractEndDate} />

      {nonSponsoredBenefits.length > 0 && <Kicker className='mt-8'>Non-sponsored benefits</Kicker>}
      {nonSponsoredBenefits.map((item, index) => {
        return <OverviewServiceCard  key={item + index} classes='mt-4' benefitType={item} contractId={selectedContractId} startDate={selectedContractStartDate} endDate={selectedContractEndDate} contractUuid={selectedContractUuid} totalCustomerCount={active_customers_count} setUilizationToBenefit={setUilizationToBenefit.bind('', NON_SPONSORED, item)} />
      })}

      <Kicker className='mt-8'>Engagement</Kicker>
      <HealthChallengeCard classes='mt-4' contractId={selectedContractId} startDate={selectedContractStartDate} endDate={selectedContractEndDate} totalCustomerCount={active_customers_count} />
      <StepsCard classes='mt-4' contractId={selectedContractId} startDate={selectedContractStartDate} endDate={selectedContractEndDate} totalCustomerCount={active_customers_count} />

      { noOfNotEnabledServices > 0 &&
        <div className='fixed bottom-0 left-0 right-0 z-30 ml-60 border-t border-gray-250 bg-white shadow-lg'>
          <div
            className='flex flex-auto cursor-pointer justify-between bg-white py-4 pl-4 pr-2'
            style={{ borderLeft: '3px solid var(--ds-amber)' }}
            onClick={toggleModal}
          >
            <span className='flex flex-auto items-center'>
              <OverviewLockIcon />
              <span className='ml-4 text-base font-fig-semibold text-gray-900'>{`${noOfNotEnabledServices} ${noOfNotEnabledServices > 1 ? 'services' : 'service'} not enabled`}</span>
              <span className='ml-2 hidden text-sm font-fig-normal text-gray-600 sm:inline'>— popular add-ons your employees don't have yet</span>
            </span>
            <OverviewChevronIcon open={state.openModel} />
          </div>
          { state.openModel && <CardSlider classes='p-2' data={notEnabledServicesData} slidesToShow={3} slidesToScroll={3} /> }
        </div>
      }
    </div>
  )
}

export default Overview
